"use client";

import { setCounterZone } from "@/lib/time/counterZone";
import { useEffect, useState } from "react";
import { dataSource } from "@/lib/data/source";
import { PACK_BY_ID, packEntitled, type PackId } from "@/lib/packs";
import type { Context } from "@/lib/api/types";
import { PosApiError, isMissingLocationClaim, posContextGap } from "@/lib/api/client";

/**
 * Why `/pos/context` did not answer.
 *
 * `unassigned` is a business-configuration problem, not an outage: the staff member is signed in
 * but has no active row in `user.pos_staff_location_assignment`, so the edge cannot compose a
 * context for them (422 INVALID_POS_CONTEXT, "missing required claim: locationId"). Retrying does
 * nothing — the session would need to be reissued with a location. Anything else (a 5xx, a
 * timeout, the network) is `outage`, where retrying is exactly the right move. The root page is
 * the one place a cashier sees this, so it is the one place that needs to tell the two apart.
 *
 * `unconfigured` is the third, and it is the one that was being thrown away. A tenant whose
 * `pos.locale` row was never written, or a place that has not been told which vertical it runs,
 * gets a 503 `POS_CONTEXT_UNAVAILABLE` carrying an accurate, actionable sentence — and it was
 * being folded into `outage`, so the counter said "Could not reach the till. Check the connection"
 * about a business that was one settings row short of trading. It is told apart by the CODE, not
 * by the 5xx status: a real outage answers 503 too, and it must keep the retry path, because
 * retrying is the right move there and the wrong one here.
 */
export type PosSessionError =
  | { kind: "unassigned" }
  | { kind: "outage" }
  | {
      kind: "unconfigured";
      /** The edge's own sentence, shown to the operator word for word. */
      reason: string;
      /** Stable key of the unset fact (`pos.locale`, `location.pack`), when the edge named one. */
      missing?: string;
      /** The signed-in staff role, so the screen offers a link only to somebody who can use it. */
      role?: string;
    };

/**
 * Who is signed in, where they are working, and which vertical this business runs.
 *
 * All three used to be invented by the client: the rail shipped a hardcoded cashier ("Priya") and
 * every screen passed its own made-up name, so a real business would have seen a stranger's name on
 * their own counter, and the vertical came from a switcher the operator could change at will.
 * `/pos/context` answers all of it from the session and the tenant's entitlements, which is the
 * only source that can be right.
 */
export type PosSession = {
  loading: boolean;
  staff?: Context["staff"];
  location?: Context["location"];
  /** The tenant's entitled packs. A business normally has exactly one. */
  packs: PackId[];
  /** The vertical to open by default — the single entitled pack, when there is one. */
  homePack?: PackId;
  /**
   * The screen ids this counter is entitled to, straight from `/pos/context`.
   *
   * Published because SWEEP-1 S1 found it answered and unread: `["checkout","members"]` for a gym,
   * `["checkout","front_desk"]` for a hotel, and a `grep` for `.screens` across this app returned
   * nothing. Route access is enforced by CounterScopeGuard, which keys off the entitled PACKS
   * because a screen id is shared between verticals that reuse each other's screens; this is here so
   * a screen that wants to ask the question can, rather than inventing its own answer.
   */
  screens: string[];
  /**
   * The tenant's own words for the nouns its manifest declares — `/pos/context`'s `vocabulary`,
   * straight from `tenant.pos_vertical_packs`.
   *
   * Published for the same reason `screens` is: it was answered on every call and read by nothing
   * (VOCAB-1). A coaching counter opening the gym screens is served
   * `{"resource":"batch","bill":"receipt"}` and said "Classes" anyway, because the only vocabulary
   * the screens could see was this app's own table. {@link useVocab} is the one caller.
   */
  vocabulary: Record<string, string>;
  /**
   * The business's default language for this counter — `pos.locale`, served as `Context.locale`.
   *
   * Required by the edge (a counter without it does not open) and read by nothing until GAP-05:
   * `CounterIntlProvider` resolves it against the person's own choice on this device.
   */
  locale?: string;
  /**
   * How this counter's bills are taxed and in what money — `/pos/context`'s `taxProfile` and
   * `currency`. Published for the settings screen, which stated a fixture tax profile (and a
   * fixture GSTIN) to every business that opened it.
   */
  taxProfile?: string;
  currency?: string;
  /**
   * Counter-readable tenant settings from `/pos/context` (`devices.enabled`), keyed by registry key.
   * Read through `devicesEnabled` in lib/devices/config.ts, never compared by hand.
   */
  settings: Readonly<Record<string, unknown>>;
  /** `ON_HOLD` when the whole account is on hold. Absent counts as active. */
  accountState?: "ACTIVE" | "ON_HOLD";
  /** Set when `/pos/context` failed. Absent on success, same as today. */
  error?: PosSessionError;
};

/**
 * One `/pos/context` per page load, shared by every caller.
 *
 * This hook is called by the rail, the scope guard, the root page, four screens and now — through
 * {@link useVocab} — by leaf components that render once per row. Without this each of those was
 * its own GET of the same unchanging answer; the salon calendar would have issued one per
 * appointment block. The counter's context does not change inside a session, so it is fetched
 * once and handed to everybody.
 *
 * Sign-in and sign-out clear it. Sign-out reloads the page anyway, but sign-in is a
 * `router.replace` — a client navigation — so without {@link resetPosContext} the next operator
 * would be handed the previous one's counter, which is the SWEEP-1 S2 defect arriving by a
 * different door.
 */
let sharedContext: Promise<Context> | undefined;

export function posContext(): Promise<Context> {
  if (!sharedContext) {
    sharedContext = dataSource.getContext().then((context) => {
      setCounterZone(context.location?.timezone);
      return context;
    }).catch((e: unknown) => {
      // A failure is not cached: the next mount (or a "Try again") asks again.
      sharedContext = undefined;
      throw e;
    });
  }
  return sharedContext;
}

/** Forget the counter's context. Called at both ends of a session. */
export function resetPosContext(): void {
  sharedContext = undefined;
  setCounterZone(undefined);
}

const NO_SETTINGS: Readonly<Record<string, unknown>> = Object.freeze({});

export function usePosSession(): PosSession {
  const [context, setContext] = useState<Context>();
  const [error, setError] = useState<PosSessionError>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    posContext()
      .then((value) => {
        if (!alive) return;
        setContext(value);
        setError(undefined);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        // Recorded, not thrown further. The rail and the customer screens also call this hook, but
        // only after the root page has already gotten a context back — they never see this branch
        // in practice, and they ignore `error` either way, so this stays a no-op for them, exactly
        // like the old blanket catch. The root page IS the first screen, so it is the one place
        // this needs to reach — that is what makes an outage there, unlike an outage deeper in a
        // shift, worth its own message instead of the screen's own data call reporting it.
        if (e instanceof PosApiError) {
          if (isMissingLocationClaim(e.problem)) {
            setError({ kind: "unassigned" });
            return;
          }
          // POS_CONTEXT_UNAVAILABLE — a counter that is not finished being set up. Read from the
          // code, so a 503 from a service that is genuinely down still falls through to `outage`.
          const gap = posContextGap(e.problem);
          if (gap) {
            setError({ kind: "unconfigured", ...gap });
            return;
          }
        }
        setError({ kind: "outage" });
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  // A pack that declares capabilities (the desk's `desk.rewards`) is shown only when the context
  // reports every one of them enabled -- hidden, never shown disabled.
  const packs = (context?.packs ?? []).filter(
    (id): id is PackId => id in PACK_BY_ID && packEntitled(id as PackId, context?.entitlements),
  );

  return {
    loading,
    staff: context?.staff,
    location: context?.location,
    packs,
    homePack: packs[0],
    screens: context?.screens ?? [],
    vocabulary: context?.vocabulary ?? {},
    locale: context?.locale,
    taxProfile: context?.taxProfile,
    // `|| undefined`: a context with a null or blank currency reads as no currency, never as a code.
    currency: context?.currency || undefined,
    settings: context?.settings ?? NO_SETTINGS,
    accountState: context?.accountState,
    error,
  };
}

/**
 * A readable job title for the person at the till.
 *
 * The session carries the RBAC role constant, and the rail was printing it verbatim — a counter
 * showing "TENANT_ADMIN" under somebody's name is leaking an implementation detail at the one place
 * a real business looks most often. Unknown roles are title-cased rather than dropped, so a new
 * role added server-side degrades to something readable instead of blank.
 */
const ROLE_LABELS: Record<string, string> = {
  TENANT_ADMIN: "Manager",
  TENANT_MANAGER: "Supervisor",
  TENANT_SUPPORT_AGENT: "Support",
  CASHIER: "Cashier",
  DESK_ATTENDANT: "Reward Desk staff",
  STAFF: "Counter staff",
};

export function staffRoleLabel(role?: string): string {
  if (!role) return "";
  return (
    ROLE_LABELS[role] ??
    role
      .toLowerCase()
      .split("_")
      .filter(Boolean)
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join(" ")
  );
}

/** Initials for the rail avatar. Falls back to a dot rather than inventing a letter. */
export function staffInitials(name?: string): string {
  if (!name) return "•";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "•";
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
